import React from 'react';
import PropTypes from 'prop-types';

import Icon from 'components/icon';
import Link from 'components/link';
import ExternalLink from 'components/link/external';
import ConsoleLink from 'components/console/link';
import DropdownButton from 'components/button/dropdown';
import StorefrontItem from 'components/storefront/storefront-item';
import StorefrontSettings from 'containers/StorefrontSettings';
import StorefrontNavigation from 'containers/StorefrontNavigation';
import StorefrontDeveloperTools from 'containers/StorefrontDeveloperTools';
import AppIcon from 'components/apps/icon';

import { confirmTabLeave, confirmRouteLeave } from 'utils/container';
import { getStorefrontURL } from 'utils/storefront';

import './storefront.scss';

export default class StorefrontView extends React.PureComponent {
  static contextTypes = {
    user: PropTypes.object.isRequired,
    openModal: PropTypes.func.isRequired,
  };

  static propTypes = {
    getInstalledApps: PropTypes.func.isRequired,
    renderThumbnail: PropTypes.func.isRequired,
    onClickPublish: PropTypes.func.isRequired,
    onClickRename: PropTypes.func.isRequired,
    onClickDuplicate: PropTypes.func.isRequired,
    onClickDelete: PropTypes.func.isRequired,
  };

  state = {
    mode: null,
    edited: false,
    tab: undefined,
  };

  componentDidMount() {
    const { location } = this.props;

    this.setState({
      tab: location?.query?.tab || 'settings',
    });

    confirmRouteLeave(this);
  }

  componentDidUpdate() {
    if (!confirmTabLeave(this, this.props)) {
      this.setState({ tab: this.props.location.query.tab });
    }
  }

  onEdited = (edited) => {
    this.setState({ edited });
  };

  renderConsoleLink(storefront) {
    const { user } = this.context;
    if (!user.devtools) {
      return null;
    }

    return (
      <ConsoleLink
        key="console"
        model=":storefronts"
        uri={storefront.id}
        icon={false}
        buttonType={null}
      >
        <span className="with-icon">
          <Icon fa="rectangle-code" />
          View in console
        </span>
      </ConsoleLink>
    );
  }

  render() {
    const { record: storefront, storefrontApp, themeApp } = this.props;
    const storefrontUrl = getStorefrontURL(storefront, true);
    const { tab, edited } = this.state;

    const viewProps = {
      uri: '/storefronts',
      sectionTitle: 'Storefronts',
      headerTitle: storefront.name || 'Storefront',
      headerComponent: (
        <StorefrontItem
          {...this.props}
          record={undefined}
          storefront={storefront}
          detail={true}
        />
      ),
      detail: true,
      headerActions: [
        {
          component: (
            <DropdownButton
              key="actions"
              type="secondary"
              anchor="right"
              anchorPosition={49}
              alignOffset={9}
              disabled={storefront.async_progressing}
              items={[
                !storefront.primary && (
                  <button
                    key="publish"
                    data-id={storefront.id}
                    onClick={this.props.onClickPublish}
                    type="button"
                  >
                    <span className="with-icon">
                      <Icon fa="circle-arrow-up" />
                      Publish
                    </span>
                  </button>
                ),
                <ExternalLink key="preview" to={storefrontUrl} icon={false}>
                  <span className="with-icon">
                    <Icon fa="external-link" />
                    Preview
                  </span>
                </ExternalLink>,
                storefront.hosted ? (
                  <Link
                    size="sm"
                    type="secondary"
                    to={`/storefronts/${storefront.id}/v1-editor`}
                  >
                    <span className="with-icon">
                      <Icon fa="paintbrush-pencil" />
                      Edit theme
                    </span>
                  </Link>
                ) : (
                  storefront.theme_id && (
                    <Link
                      size="sm"
                      type={storefront.primary ? 'default' : 'secondary'}
                      to={`/storefronts/${storefront.id}/editor`}
                    >
                      <span className="with-icon">
                        <Icon fa="paintbrush-pencil" />
                        Edit theme
                      </span>
                    </Link>
                  )
                ),
                themeApp && (
                  <Link to={`/storefronts/${storefront.id}/code`}>
                    <span className="with-icon">
                      <Icon fa="rectangle-code" />
                      Edit code
                    </span>
                  </Link>
                ),
                this.renderConsoleLink(storefront),
                <hr key="hr-actions" />,
                <button
                  key="rename"
                  data-id={storefront.id}
                  onClick={this.props.onClickRename}
                  type="button"
                >
                  Rename
                </button>,
                <button
                  key="duplicate"
                  data-id={storefront.id}
                  onClick={this.props.onClickDuplicate}
                  type="button"
                >
                  Duplicate
                </button>,
                ...(!storefront.primary
                  ? [
                      <hr key="hr-delete" />,
                      <button
                        key="remove"
                        data-id={storefront.id}
                        onClick={this.props.onClickDelete}
                        className="danger"
                        type="button"
                      >
                        Delete storefront
                      </button>,
                    ]
                  : []),
                ...(storefrontApp && themeApp
                  ? [
                      <hr key="hr-app" />,
                      <label>Storefront app</label>,
                      <Link
                        to={`/apps/${storefrontApp.app_id}`}
                        className="storefront-dropdown-app-indicator"
                      >
                        <AppIcon app={storefrontApp.app} size={20} />{' '}
                        {storefrontApp.app.name}{' '}
                        {storefrontApp.app_version || storefrontApp.app.version}
                      </Link>,
                    ]
                  : []),
              ]}
            >
              Actions
            </DropdownButton>
          ),
        },
      ],
      tabs: {
        default: {
          label: 'Settings',
        },
        navigation: {
          label: 'Navigation',
        },
        devtools: (storefront.hosted || storefront.external?.custom) && {
          label: 'Developer',
        },
      },
    };

    switch (tab) {
      case 'navigation':
        return (
          <div className="storefront-view">
            <StorefrontNavigation
              {...this.props}
              record={undefined}
              storefront={storefront}
              viewProps={viewProps}
              edited={edited}
              onEdited={this.onEdited}
            />
          </div>
        );
      case 'devtools':
        return (
          <div className="storefront-view">
            <StorefrontDeveloperTools
              {...this.props}
              viewProps={viewProps}
              edited={edited}
              onEdited={this.onEdited}
            />
          </div>
        );
      case 'settings':
      default:
        return (
          <div className="storefront-view">
            <StorefrontSettings
              {...this.props}
              viewProps={viewProps}
              edited={edited}
              onEdited={this.onEdited}
            />
          </div>
        );
    }
  }
}
